// Smart Sash Admin Panel - Certification Question Editor Component
// Comprehensive form for editing individual question data

import React from 'react';
import { CertificationQuestionParsed, QuestionType } from '@/models';
import { getInputBgClass } from '@/utils/certification';
import { Button } from '@/components/common';
import { AnswerList } from './AnswerList';

export interface QuestionEditorProps {
  question: CertificationQuestionParsed | null;
  questionNumber: number;
  totalQuestions: number;
  isEditMode: boolean;
  onQuestionChange: (updates: Partial<CertificationQuestionParsed>) => void;
  onDelete?: () => void;
}

export const QuestionEditor: React.FC<QuestionEditorProps> = ({
  question,
  questionNumber,
  totalQuestions,
  isEditMode,
  onQuestionChange,
  onDelete,
}) => {
  const inputBgClass = getInputBgClass(isEditMode);

  // Handle answer text change
  const handleAnswerTextChange = (answerId: string, newText: string) => {
    if (!question) return;
    const newAnswers = question.answers.map((a) =>
      a.id === answerId ? { ...a, text: newText } : a
    );
    onQuestionChange({ answers: newAnswers });
  };

  // Handle correct answer change
  const handleCorrectAnswerChange = (newCorrectId: string) => {
    if (!question) return;
    onQuestionChange({
      correctAnswer: newCorrectId,
      answers: question.answers.map((a) => ({
        ...a,
        isCorrect: a.id === newCorrectId,
      })),
    });
  };

  // Empty state when no question selected
  if (!question) {
    return (
      <div className="text-center py-5 text-muted">
        <i className="fas fa-question-circle fa-3x mb-3"></i>
        <p className="mb-0">No question selected</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header: Question number and Active status */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="mb-0">
          <span className="text-primary">#{questionNumber}</span>
          <span className="text-muted mx-2">/</span>
          <span className="text-muted">{totalQuestions}</span>
        </h5>
        <div className="d-flex align-items-center gap-3">
          {/* Enabled checkbox */}
          <div className="form-check mb-0">
            <input
              type="checkbox"
              className={`form-check-input ${inputBgClass}`}
              id="questionEnabled"
              checked={question.isActive}
              onChange={(e) => onQuestionChange({ isActive: e.target.checked })}
              disabled={!isEditMode}
            />
            <label className="form-check-label" htmlFor="questionEnabled">
              Enabled
            </label>
          </div>
          {/* Status badge */}
          {question.isActive ? (
            <span className="badge bg-success">Active</span>
          ) : (
            <span className="badge bg-secondary">Inactive</span>
          )}
          {/* Delete button (edit mode only) */}
          {isEditMode && onDelete && (
            <Button
              variant="danger"
              outline
              size="sm"
              icon="fas fa-trash"
              onClick={onDelete}
              title="Delete this question"
            >
              Delete
            </Button>
          )}
        </div>
      </div>

      <div className="row">
        {/* Left column: Question text and answers */}
        <div className="col-lg-8">
          {/* Question Text */}
          <div className="mb-4">
            <label className="form-label fw-semibold">Question Text</label>
            {isEditMode ? (
              <textarea
                className={`form-control ${inputBgClass}`}
                rows={4}
                value={question.question || ''}
                onChange={(e) => onQuestionChange({ question: e.target.value })}
                placeholder="Enter your question here..."
              />
            ) : (
              <div className="form-control-plaintext border rounded p-3 bg-light min-height-100">
                {question.question || <em className="text-muted">No question text</em>}
              </div>
            )}
          </div>

          {/* Answer Options (a through h) */}
          <AnswerList
            answers={question.answers}
            correctAnswer={question.correctAnswer}
            isEditMode={isEditMode}
            inputBgClass={inputBgClass}
            onAnswerTextChange={handleAnswerTextChange}
            onCorrectAnswerChange={handleCorrectAnswerChange}
          />
        </div>

        {/* Right column: Settings and explanation */}
        <div className="col-lg-4">
          {/* Correct Answer dropdown */}
          <div className="mb-3">
            <label className="form-label fw-semibold">Correct Answer</label>
            {isEditMode ? (
              <select
                className={`form-select ${inputBgClass}`}
                value={question.correctAnswer || ''}
                onChange={(e) => handleCorrectAnswerChange(e.target.value)}
              >
                <option value="">Select correct answer...</option>
                {question.answers.map((answer) => (
                  <option key={answer.id} value={answer.id}>
                    {answer.id.toUpperCase()}: {answer.text || '(empty)'}
                  </option>
                ))}
              </select>
            ) : (
              <div className="form-control-plaintext border rounded p-2 bg-light">
                {question.correctAnswer?.toUpperCase() || 'Not set'}
              </div>
            )}
          </div>

          {/* Points */}
          <div className="mb-3">
            <label className="form-label fw-semibold">Points</label>
            {isEditMode ? (
              <input
                type="number"
                className={`form-control ${inputBgClass}`}
                value={question.points}
                onChange={(e) =>
                  onQuestionChange({ points: parseInt(e.target.value) || 0 })
                }
                min={0}
              />
            ) : (
              <div className="form-control-plaintext border rounded p-2 bg-light">
                {question.points}
              </div>
            )}
          </div>

          {/* Question Type */}
          <div className="mb-3">
            <label className="form-label fw-semibold">Question Type</label>
            {isEditMode ? (
              <select
                className={`form-select ${inputBgClass}`}
                value={question.questionType || QuestionType.MultipleChoice}
                onChange={(e) => onQuestionChange({ questionType: e.target.value })}
              >
                <option value={QuestionType.MultipleChoice}>Multiple Choice</option>
                <option value={QuestionType.TrueFalse}>True/False</option>
                <option value={QuestionType.ShortAnswer}>Short Answer</option>
                <option value={QuestionType.Essay}>Essay</option>
              </select>
            ) : (
              <div className="form-control-plaintext border rounded p-2 bg-light">
                {question.questionType || 'MultipleChoice'}
              </div>
            )}
          </div>

          {/* Explanation (UI-only, not persisted to API) */}
          <div className="mb-3">
            <label className="form-label fw-semibold">
              Explanation
              <span className="text-muted small ms-2">(UI only)</span>
            </label>
            {isEditMode ? (
              <textarea
                className={`form-control ${inputBgClass}`}
                rows={3}
                placeholder="Optional explanation for the correct answer..."
                disabled
              />
            ) : (
              <div className="form-control-plaintext border rounded p-2 bg-light text-muted fst-italic">
                Not available
              </div>
            )}
            <small className="text-muted">
              Explanation field will be available in a future update.
            </small>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestionEditor;
